import { FC } from 'react';
import {
    type ButtonWithIconProps,
    type ButtonProps as ZdsButtonProps,
    ButtonRight,
    ButtonLeft,
    Button,
} from '@zoopla-eng/zds-components/button';
import { buttonWrapperStyle } from './ButtonWithWrapper.styles.css';

export type ButtonCategory = Extract<
    ZdsButtonProps['category'],
    'primary' | 'secondary'
>;

const DEFAULT_ALIGNMENT: ButtonAlignmentWithBreakpoints = {
    default: 'fill',
};

export type ButtonAlignment = 'fill' | 'left';
export type ButtonAlignmentWithBreakpoints = {
    default: ButtonAlignment;
    bpS200?: ButtonAlignment;
    bpM100?: ButtonAlignment;
    bpL100?: ButtonAlignment;
};

export type ButtonProps = Pick<ZdsButtonProps, 'loading' | 'loadingText'> & {
    Icon?: ButtonWithIconProps['Icon'];
    text: string;
    iconPosition?: 'right' | 'left';
};

type WrapperProps = {
    alignment?: ButtonAlignmentWithBreakpoints;
    category: ButtonCategory;
};

// Integration tests for this component live within AddressSelection
export const ButtonWithWrapper: FC<ButtonProps & WrapperProps> = ({
    alignment = DEFAULT_ALIGNMENT,
    text,
    Icon,
    iconPosition = 'left',
    loading,
    loadingText,
    category,
}) => {
    // The ZDS button requires loadingText to exist if loading is used, so we
    // have to narrow these types here. In practice we control these values in
    // this component and they are not exposed outside of the shared component,
    // so this is a safe narrow to make here without warning end users.
    const loadingProps =
        loading && loadingText ? { loading: true, loadingText } : {};

    const props = {
        category,
        children: text,
        ...loadingProps,
    } satisfies ZdsButtonProps;

    return (
        <div className={buttonWrapperStyle(alignment)}>
            {Icon ? (
                <>
                    {iconPosition === 'left' && (
                        <ButtonLeft Icon={Icon} {...props} />
                    )}
                    {iconPosition === 'right' && (
                        <ButtonRight Icon={Icon} {...props} />
                    )}
                </>
            ) : (
                <Button {...props} />
            )}
        </div>
    );
};
